import { DietaryPrefs } from '@hungryroot/dietary-restrictions';
import { type ContainerFrequencyOptions } from '@hungryroot/rest';
import {
  ADULTS_COUNT_PROP,
  CHILDREN_COUNT_PROP,
  YOUNG_CHILDREN_COUNT_PROP,
} from '../questionProps';

export enum HouseholdSize {
  ME = 1,
  ME_PLUS_ONE = 2,
  ME_PLUS_MORE = 3,
}

export enum Goals {
  SaveTime = 5,
  ImproveHealth = 2,
  LoseWeight = 3,
  TryNewThings = 9,
  EatLessTakeout = 10,
  GroceryShopLess = 4,
  WasteLessFood = 11,
  SaveMoney = 7,
  EatMoreVeggiesWithFamily = 8,
  SimplifyCooking = 12,
  LowerStress = 13,
  GLP1Support = 14,
}

export enum DietaryRestrictions {
  Omnivore = 11,
  Vegan = 1,
  Vegetarian = 2,
  Pescatarian = 3,
  DairyFree = 4,
  GlutenFree = 5,
  SoyFree = 6,
  TreeNutFree = 7,
  PeanutFree = 8,
  EggFree = 9,
  ShellfishFree = 10,
  Flexitarian = 12,
  SesameFree = 15,
  GrainFree = 17,
  GarlicFree = 13,
  OnionFree = 14,
  CaffeineFree = 16,
}

export enum EatingHabit {
  Omnivore = 'omnivore',
  Flexitarian = 'flexitarian',
  Pescatarian = 'pescatarian',
  Vegetarian = 'vegetarian',
  Vegan = 'vegan',
}

export const EatingHabitToDietKeys = {
  [EatingHabit.Omnivore]: DietaryPrefs.Omnivore,
  [EatingHabit.Flexitarian]: DietaryPrefs.Flexitarian,
  [EatingHabit.Pescatarian]: DietaryPrefs.Pescatarian,
  [EatingHabit.Vegetarian]: DietaryPrefs.Vegetarian,
  [EatingHabit.Vegan]: DietaryPrefs.Vegan,
};

export enum Dietary {
  DairyFree = 'dairy-free',
  GlutenFree = 'gluten-free',
  SoyFree = 'soy-free',
  TreeNutFree = 'tree-nut-free',
  PeanutFree = 'peanut-free',
  EggFree = 'egg-free',
  ShellfishFree = 'shellfish-free',
  SesameFree = 'sesame-free',
  GrainFree = 'grain-free',
  GarlicFree = 'garlic-free',
  OnionFree = 'onion-free',
  CaffeineFree = 'caffeine-free',
}

export const DietaryToDietaryPrefs = {
  [Dietary.DairyFree]: DietaryPrefs.DairyFree,
  [Dietary.GlutenFree]: DietaryPrefs.GlutenFree,
  [Dietary.SoyFree]: DietaryPrefs.SoyFree,
  [Dietary.TreeNutFree]: DietaryPrefs.TreeNutFree,
  [Dietary.PeanutFree]: DietaryPrefs.PeanutFree,
  [Dietary.EggFree]: DietaryPrefs.EggFree,
  [Dietary.ShellfishFree]: DietaryPrefs.ShellfishFree,
  [Dietary.SesameFree]: DietaryPrefs.SesameFree,
  [Dietary.GrainFree]: DietaryPrefs.GrainFree,
  [Dietary.GarlicFree]: DietaryPrefs.GarlicFree,
  [Dietary.OnionFree]: DietaryPrefs.OnionFree,
  [Dietary.CaffeineFree]: DietaryPrefs.CaffeineFree,
};

export type TagId = number;

export enum FoodPrep {
  Simple = 'simple',
  ChoppingOrPrepping = 'chopping-or-prepping',
}

export interface ProteinTagValue {
  id: TagId;
  liked: LikeDislikeTagsRatings;
}

export interface ProteinTagTernaryValue {
  id: TagId;
  rated: ProteinTagTernaryRating;
}

export enum ProteinTagTernaryRating {
  NoThanks = -1,
  SoSo = 0,
  Absolutely = 1,
}

export const LikeDislikeTags = {
  NoRating: 0,
  Liked: 1,
  Disliked: -1,
} as const;

type LikeDislikeTagsRatings =
  (typeof LikeDislikeTags)[keyof typeof LikeDislikeTags];

const KID_SNACKS_QUESTION = 'kidsSnacks';
const KID_SNACKS_FREQUENCY_QUESTION = 'kidsSnacksFrequency';

type KidSnackQuiz<T> = {
  [KID_SNACKS_QUESTION]: number[];
  [KID_SNACKS_FREQUENCY_QUESTION]: T | null;
};

type Nullable<T> = { [K in keyof T]: T[K] | null };

export type LunchDinnerPick = { id: number; type?: 'product' | 'pairing' };

export type NonNullableQuiz = {
  householdSize: HouseholdSize;
  [ADULTS_COUNT_PROP]: number;
  [CHILDREN_COUNT_PROP]: number;
  [YOUNG_CHILDREN_COUNT_PROP]: number;
  goals: Goals[];
  budget?: TagId;
  habits: EatingHabit;
  dietary: Dietary[];
  macroNutrient: TagId[];
  foodQualities: TagId[];
  foodFormats: TagId[];
  foodPref: FoodPrep;
  lunchPicks: LunchDinnerPick[];
  dinnerPicks: LunchDinnerPick[];

  // Tag Based
  cuisines: ProteinTagValue[];
  flavors: ProteinTagValue[];
  meats: ProteinTagTernaryValue[];
  seafood: ProteinTagTernaryValue[];
  plants: ProteinTagTernaryValue[];
  dislike: TagId[];
  breakfast: TagId[];
  breakfastFrequency: ContainerFrequencyOptions;
  lunchDinner: TagId[];
  lunch?: TagId[];
  dinner?: TagId[];
  lunchFrequency: ContainerFrequencyOptions;
  dinnerFrequency: ContainerFrequencyOptions;
  readyMade: TagId[];
  snacks: TagId[];
  snacksFrequency: ContainerFrequencyOptions;
  sweets: TagId[];
  sweetsFrequency: ContainerFrequencyOptions;
} & Partial<KidSnackQuiz<ContainerFrequencyOptions>>;

export type Quiz = Nullable<NonNullableQuiz>;

export const defaultQuiz: Quiz = {
  householdSize: null,
  [ADULTS_COUNT_PROP]: 1,
  [CHILDREN_COUNT_PROP]: null,
  [YOUNG_CHILDREN_COUNT_PROP]: null,
  goals: null,
  budget: null,
  habits: null,
  dietary: null,
  macroNutrient: null,
  foodQualities: null,
  foodFormats: null,
  foodPref: null,
  cuisines: null,
  flavors: null,
  meats: null,
  seafood: null,
  plants: null,
  lunchPicks: null,
  dinnerPicks: null,
  dislike: null,
  breakfast: null,
  breakfastFrequency: null,
  lunchDinner: null,
  lunch: null,
  dinner: null,
  lunchFrequency: null,
  dinnerFrequency: null,
  readyMade: null,
  snacks: null,
  sweets: null,
  snacksFrequency: null,
  sweetsFrequency: null,
  [KID_SNACKS_FREQUENCY_QUESTION]: null,
};
